import React, { useState } from "react";
import { Box, List, ListItem, ListItemButton, ListItemText, Typography, Collapse, useTheme } from "@mui/material";
import Image from "next/image";
import { useRouter } from "next/router";
import { getFilteredMenuItems, MenuItem } from "@/lib/utils";
import { KeyboardArrowDown, LogoutOutlined as LogoutIcon } from "@mui/icons-material";
import { logoutUser } from "@/Api/ClientHelper";
import { useConfirm } from "material-ui-confirm";
import { useAppSelector } from "@/Redux/app/hooks";

interface SidebarProps {
    onMobileClose?: () => void;
}

const Sidebar = ({ onMobileClose }: SidebarProps) => {
    // ============= Hooks =============
    const theme = useTheme();
    const confirmDialogue = useConfirm();
    const userDetail = useAppSelector((state) => state.auth.loggedInUserDetails.userDetail);
    const userRole = userDetail?.userType || "";
    const { pathname, push } = useRouter();

    // ============= States =============
    const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>({ menu: false });


    // ============= Handlers =============
    const isActive = (path: string) => path === "/dashboard" ? pathname === "/dashboard" : pathname.startsWith(path);

    const handleNavigation = (path: string) => {
        push(path);
        if (onMobileClose) onMobileClose();
    };

    const handleLogout = () => {
        confirmDialogue({
            title: "Logout?",
            description: "Are you sure you want to logout?",
            confirmationText: "Logout",
            cancellationText: "Cancel",
            confirmationButtonProps: { color: "error" },
        }).then((res: { confirmed?: boolean }) => {
            if (res?.confirmed) logoutUser();
        });
    };

    const filteredMenuItems = getFilteredMenuItems(userRole);

    return (
        <Box sx={{
            width: 280,
            height: "100vh",
            bgcolor: theme.palette.secondary.contrastText,
            display: "flex",
            flexDirection: "column",
            p: 2.5,
            position: { xs: "relative", lg: "fixed" },
            left: 0,
            top: 0,
            zIndex: 1200,
            overflowY: "auto",
            scrollbarWidth: "none",
            borderRight: `1px solid ${theme.palette.divider}`,
            "&::-webkit-scrollbar": { display: "none" },
        }}>
            <Box sx={{ pl: 0.5, pb: 2 }}>
                <Image src="/images/Full_Logo.svg" alt="Logo" width={160} height={60} priority style={{ objectFit: "contain" }} />
            </Box>

            <List sx={{ flexGrow: 1, p: 0 }}>
                {filteredMenuItems.map((item: MenuItem) => {
                    const active = isActive(item.path);
                    const hasSub = !!item.subMenu?.length;
                    const isOpen = openSubMenus[item.id];

                    return (
                        <React.Fragment key={item.id}>
                            <ListItem disablePadding sx={{ mb: 1 }}>
                                <ListItemButton
                                    onClick={() => hasSub ? setOpenSubMenus((p: Record<string, boolean>) => ({ ...p, [item.id]: !p[item.id] })) : handleNavigation(item.path)}
                                    sx={{
                                        borderRadius: "8px", p: "10px 8px 10px 16px",
                                        bgcolor: active ? theme.palette.primary.main : "transparent",
                                        color: active ? theme.palette.custom.white : theme.palette.custom.lightGray,
                                        "&:hover": { bgcolor: active ? theme.palette.primary.main : theme.palette.secondary.main },
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Box sx={{ width: 20, height: 20, position: "relative", filter: active ? "brightness(0) invert(1)" : "none" }}>
                                                <Image src={`/images/${item.icon}`} alt={item.label} fill style={{ objectFit: "contain" }} />
                                            </Box>
                                            <Typography sx={{ fontSize: "14px", fontWeight: active ? 700 : 500 }}>
                                                {item.label}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flexGrow: 1 }} />
                                        {/* {item.badge &&
                                            <Box
                                                sx={{
                                                    bgcolor: active ? theme.palette.custom.white : theme.palette.primary.main,
                                                    color: active ? theme.palette.primary.main : theme.palette.custom.white,
                                                    borderRadius: "50%",
                                                    width: 20,
                                                    height: 20,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "10px",
                                                    fontWeight: 400,
                                                }}>
                                                {item.badge}
                                            </Box>
                                        } */}
                                        {hasSub && <KeyboardArrowDown sx={{ fontSize: "20px", transform: isOpen ? "rotate(180deg)" : "none", transition: "0.2s" }} />}
                                    </Box>
                                </ListItemButton>
                            </ListItem>

                            {hasSub && (
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding sx={{ pl: 4, pr: 1 }}>
                                        {item.subMenu?.map((sub: MenuItem) => {
                                            const subActive = pathname === sub.path;
                                            return (
                                                <ListItemButton
                                                    key={sub.id}
                                                    onClick={() => handleNavigation(sub.path)}
                                                    sx={{
                                                        borderRadius: "16px",
                                                        mb: "4px",
                                                        p: "6px 8px 6px 16px",
                                                        bgcolor: subActive ? theme.palette.custom.white : "transparent",
                                                        color: subActive ? theme.palette.primary.main : theme.palette.custom.lightGray,
                                                        "&:hover": { bgcolor: subActive ? theme.palette.custom.white : theme.palette.secondary.main }
                                                    }}
                                                >
                                                    <ListItemText primary={<Typography sx={{ fontSize: "14px", fontWeight: subActive ? 700 : 500 }}>{sub.label}</Typography>} />
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                </Collapse>
                            )}
                        </React.Fragment>
                    );
                })}
            </List>

            <Box>
                <ListItemButton
                    onClick={handleLogout}
                    sx={{
                        borderRadius: "8px",
                        bgcolor: theme.palette.background.default,
                        display: "flex",
                        alignItems: "start",
                        justifyContent: "start",
                        py: "10px",
                        "&:hover": { bgcolor: theme.palette.secondary.main }
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <LogoutIcon sx={{ color: theme.palette.custom.lightGray, fontSize: "20px" }} />
                        <Typography sx={{ fontSize: "14px", fontWeight: 500, color: theme.palette.custom.lightGray }}>Logout</Typography>
                    </Box>
                </ListItemButton>
            </Box>
        </Box>
    );
};

export default Sidebar;
